import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Bell, Plus, Edit2, Trash2, X, Save, ArrowLeft, Loader2, Search,
  Send, Pause, Play, Clock, Mail, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELIVERY_BADGES = {
  immediate: { label: 'Immediate', class: 'bg-blue-100 text-blue-700' },
  daily_digest: { label: 'Daily', class: 'bg-amber-100 text-amber-700' },
  weekly_digest: { label: 'Weekly', class: 'bg-purple-100 text-purple-700' },
};

// Schedule presets surfaced as flat dropdown options. Each option maps to a
// (delivery, days[]) pair so users see "Tue/Thu", "Mon/Wed/Fri", "Weekdays"
// without having to discover the day chips.
const SCHEDULE_PRESETS = [
  { value: 'immediate',     label: 'Immediate (send right away)',          delivery: 'immediate',     days: null },
  { value: 'daily',         label: 'Every day digest',                     delivery: 'daily_digest',  days: [0,1,2,3,4,5,6] },
  { value: 'weekdays',      label: 'Weekdays only (Mon–Fri)',              delivery: 'daily_digest',  days: [0,1,2,3,4] },
  { value: 'tue_thu',       label: '2× per week (Tue & Thu)',              delivery: 'daily_digest',  days: [1,3] },
  { value: 'mon_wed_fri',   label: '3× per week (Mon, Wed, Fri)',          delivery: 'daily_digest',  days: [0,2,4] },
  { value: 'weekly',        label: 'Once a week (pick one day)',           delivery: 'weekly_digest', days: null },
  { value: 'custom',        label: 'Custom schedule (pick your own days)', delivery: 'daily_digest',  days: null },
];

const presetFromRule = (delivery, days) => {
  if (delivery === 'immediate') return 'immediate';
  if (delivery === 'weekly_digest') return 'weekly';
  const d = (days || []).slice().sort();
  if (d.length === 7) return 'daily';
  if (d.length === 5 && [0,1,2,3,4].every(x => d.includes(x))) return 'weekdays';
  if (d.length === 2 && d[0] === 1 && d[1] === 3) return 'tue_thu';
  if (d.length === 3 && d[0] === 0 && d[1] === 2 && d[2] === 4) return 'mon_wed_fri';
  return 'custom';
};

const DOW_LABEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DOW_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const summarizeDays = (days) => {
  if (!Array.isArray(days) || days.length === 0) return '—';
  if (days.length === 7) return 'Every day';
  if (days.length === 5 && [0,1,2,3,4].every(d => days.includes(d))) return 'Weekdays';
  return days.slice().sort().map(d => DOW_LABEL[d]).join('/');
};

const fmtTime = (h, m) => {
  const hr = h % 12 === 0 ? 12 : h % 12;
  const ap = h < 12 ? 'am' : 'pm';
  return `${hr}:${String(m).padStart(2, '0')}${ap}`;
};

const EVENT_LABELS = {
  note_added: 'Note Added',
  credential_expiring: 'Credential Expiring',
  pto_requested: 'PTO Request',
  applicant_added: 'New Applicant',
};

const SCOPE_LABELS = {
  applicant: 'Applicant',
  personnel: 'Personnel',
  agency: 'Agency',
  all: 'All',
};

// Searchable recipient multi-select (personnel + system users)
function RecipientPicker({ pool, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isSelected = (p) => selected.some(s => (s.email || '').toLowerCase() === (p.email || '').toLowerCase());
  const toggle = (p) => {
    if (!p.email) return;
    if (isSelected(p)) {
      onChange(selected.filter(s => s.email.toLowerCase() !== p.email.toLowerCase()));
    } else {
      onChange([...selected, {
        personnel_id: p.personnel_id,
        name: p.name,
        email: p.email,
      }]);
    }
  };

  const filtered = pool.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (p.name || '').toLowerCase().includes(s) || (p.email || '').toLowerCase().includes(s);
  });

  return (
    <div ref={ref} className="relative" data-testid="personnel-picker">
      <div
        className="min-h-[42px] border rounded-md px-2 py-1.5 flex flex-wrap gap-1 cursor-text bg-background"
        onClick={() => setOpen(true)}
      >
        {selected.length === 0 && (
          <span className="text-sm text-muted-foreground self-center">Click to add recipients...</span>
        )}
        {selected.map(s => (
          <span
            key={s.email}
            className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-md"
            data-testid={`recipient-chip-${s.email}`}
          >
            {s.name}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(selected.filter(x => x.email !== s.email)); }}
              className="hover:bg-primary/20 rounded p-0.5"
            >
              <X size={10} />
            </button>
          </span>
        ))}
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-background border rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-7 h-8"
                autoFocus
                data-testid="personnel-search-input"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground p-3 text-center">No matches</p>
            )}
            {filtered.map(p => (
              <button
                key={p.email}
                type="button"
                onClick={() => toggle(p)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 ${isSelected(p) ? 'bg-primary/5' : ''}`}
                data-testid={`personnel-option-${p.email}`}
              >
                <input type="checkbox" checked={isSelected(p)} readOnly className="rounded" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    {p.name}
                    {p.source === 'user' && (
                      <span className="text-[9px] px-1.5 py-0 rounded bg-blue-50 text-blue-700 border border-blue-200">
                        {p.role || 'user'}
                      </span>
                    )}
                    {p.source === 'personnel+user' && (
                      <span className="text-[9px] px-1.5 py-0 rounded bg-green-50 text-green-700 border border-green-200">
                        personnel + {p.role || 'user'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{p.email}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function NotificationRules() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [systemNotifs, setSystemNotifs] = useState([]);
  const [recipientPool, setRecipientPool] = useState([]);
  const [options, setOptions] = useState({ event_types: [], scopes: [], deliveries: [], days_of_week: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    event_type: 'note_added',
    scope: 'agency',
    delivery: 'immediate',
    schedule_hour: 8,
    schedule_minute: 0,
    schedule_day_of_week: 0,
    schedule_days_of_week: [0, 1, 2, 3, 4, 5, 6],
    send_when_empty: true,
    recipients: [],
  });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [rulesRes, poolRes, optionsRes, sysRes] = await Promise.all([
        axios.get(`${API}/notification-rules`, { headers }),
        axios.get(`${API}/notification-rules/recipient-pool`, { headers }),
        axios.get(`${API}/notification-rules/options`, { headers }),
        axios.get(`${API}/system-notifications`, { headers }).catch(() => ({ data: [] })),
      ]);
      setRules(rulesRes.data || []);
      setRecipientPool(poolRes.data || []);
      setOptions(optionsRes.data);
      setSystemNotifs(sysRes.data || []);
    } catch (e) {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      event_type: 'note_added',
      scope: 'agency',
      delivery: 'immediate',
      schedule_hour: 8,
      schedule_minute: 0,
      schedule_day_of_week: 0,
      schedule_days_of_week: [0, 1, 2, 3, 4, 5, 6],
      send_when_empty: true,
      recipients: [],
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.recipients.length === 0) { toast.error('Pick at least one recipient'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/notification-rules/${editingId}`, form);
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/notification-rules`, form);
        toast.success('Rule created');
      }
      resetForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name,
      description: r.description || '',
      event_type: r.event_type,
      scope: r.scope,
      delivery: r.delivery,
      schedule_hour: r.schedule_hour ?? 8,
      schedule_minute: r.schedule_minute ?? 0,
      schedule_day_of_week: r.schedule_day_of_week ?? 0,
      schedule_days_of_week: (r.schedule_days_of_week && r.schedule_days_of_week.length)
        ? r.schedule_days_of_week
        : (r.delivery === 'weekly_digest' ? [r.schedule_day_of_week ?? 0] : [0, 1, 2, 3, 4, 5, 6]),
      send_when_empty: r.send_when_empty !== false, // default true
      recipients: r.recipients || [],
    });
    setShowForm(true);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete the "${r.name}" rule?`)) return;
    try {
      await axios.delete(`${API}/notification-rules/${r.id}`);
      toast.success('Rule deleted');
      fetchAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleToggle = async (r) => {
    try {
      await axios.put(`${API}/notification-rules/${r.id}`, { is_active: !r.is_active });
      fetchAll();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleSendNow = async (r) => {
    try {
      const res = await axios.post(`${API}/notification-rules/${r.id}/send-digest`);
      if (res.data.sent) {
        toast.success(`Sent ${res.data.items} item(s) to ${res.data.recipients} recipient(s)`);
      } else {
        toast.info(res.data.reason || 'Nothing queued to send');
      }
      fetchAll();
    } catch (e) {
      toast.error('Send failed');
    }
  };

  const handleImportLegacy = async () => {
    if (!window.confirm('Import recipients from the legacy Notification Settings page as editable rules? Existing rules with the same name will be skipped.')) return;
    try {
      const res = await axios.post(`${API}/notification-rules/migrate-from-legacy`);
      const { created, skipped } = res.data;
      if (created > 0) {
        toast.success(`Imported ${created} rule(s) from legacy settings`);
      } else if (skipped > 0) {
        toast.info(`Already imported (${skipped} skipped)`);
      } else {
        toast.info('No legacy recipients found to import');
      }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">
          <ArrowLeft size={16} className="mr-1" /> Back to Admin
        </Button>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
            <Bell className="text-primary" /> Notification Rules
          </h1>
          <p className="text-muted-foreground mt-1">
            Build flexible rules that route emails to any combination of personnel for any event.
            Same event can have multiple rules (e.g., immediate alerts to one team, weekly digest to another).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleImportLegacy} data-testid="import-legacy-btn">
            Import Legacy Settings
          </Button>
          <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-rule-btn">
            <Plus size={16} className="mr-1" /> Add Rule
          </Button>
        </div>
      </div>

      {showForm && (
        <Card className="p-6" data-testid="rule-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">{editingId ? 'Edit Rule' : 'Add Rule'}</h3>
            <Button variant="ghost" size="sm" onClick={resetForm}><X size={16} /></Button>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input
                placeholder="e.g. Weekly Agency Notes Digest"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="rule-name-input"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="What this rule does"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="rule-description-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Event</Label>
                <select
                  value={form.event_type}
                  onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                  className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  data-testid="rule-event-select"
                >
                  {options.event_types.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Scope</Label>
                <select
                  value={form.scope}
                  onChange={(e) => setForm({ ...form, scope: e.target.value })}
                  className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  data-testid="rule-scope-select"
                >
                  {options.scopes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Schedule</Label>
                <select
                  value={presetFromRule(form.delivery, form.schedule_days_of_week)}
                  onChange={(e) => {
                    const p = SCHEDULE_PRESETS.find(x => x.value === e.target.value);
                    if (!p) return;
                    setForm(f => ({
                      ...f,
                      delivery: p.delivery,
                      // null days means "keep current selection" (custom or weekly)
                      schedule_days_of_week: p.days === null
                        ? (p.value === 'weekly'
                            ? [f.schedule_day_of_week ?? 0]
                            : f.schedule_days_of_week)
                        : p.days,
                    }));
                  }}
                  className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  data-testid="rule-delivery-select"
                >
                  {SCHEDULE_PRESETS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {/* Schedule (only meaningful for digests) */}
            {form.delivery !== 'immediate' && (
              <div className="space-y-3 p-3 rounded-lg bg-muted/40 border" data-testid="schedule-fields">
                <div>
                  <Label>Days to send {form.delivery === 'weekly_digest' && <span className="text-xs text-muted-foreground font-normal">(pick one for weekly)</span>}</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="day-chips">
                    {DOW_LABEL.map((d, i) => {
                      const active = form.schedule_days_of_week.includes(i);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            if (form.delivery === 'weekly_digest') {
                              // Weekly = exactly one day
                              setForm({ ...form, schedule_days_of_week: [i], schedule_day_of_week: i });
                              return;
                            }
                            const cur = form.schedule_days_of_week;
                            const next = active ? cur.filter(x => x !== i) : [...cur, i];
                            setForm({ ...form, schedule_days_of_week: next });
                          }}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                            active
                              ? 'bg-primary text-white border-primary'
                              : 'bg-background border-input hover:bg-muted'
                          }`}
                          data-testid={`day-chip-${i}`}
                          title={DOW_FULL[i]}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    Tap chips to fine-tune the schedule. Current selection: <b>{summarizeDays(form.schedule_days_of_week)}</b>
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Hour (24h)</Label>
                    <select
                      value={form.schedule_hour}
                      onChange={(e) => setForm({ ...form, schedule_hour: parseInt(e.target.value) })}
                      className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                      data-testid="rule-hour-select"
                    >
                      {Array.from({ length: 24 }, (_, i) => i).map(h => (
                        <option key={h} value={h}>
                          {h.toString().padStart(2, '0')}:00 ({fmtTime(h, 0)})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Minute</Label>
                    <select
                      value={form.schedule_minute}
                      onChange={(e) => setForm({ ...form, schedule_minute: parseInt(e.target.value) })}
                      className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                      data-testid="rule-minute-select"
                    >
                      {[0, 15, 30, 45].map(m => (
                        <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <label className="flex items-start gap-2 cursor-pointer" data-testid="send-when-empty-label">
                  <input
                    type="checkbox"
                    checked={form.send_when_empty}
                    onChange={(e) => setForm({ ...form, send_when_empty: e.target.checked })}
                    className="h-4 w-4 mt-0.5 rounded border-gray-300"
                    data-testid="send-when-empty-checkbox"
                  />
                  <div>
                    <span className="text-sm font-medium">Send even if there are no new items</span>
                    <p className="text-xs text-muted-foreground">
                      Recipients will still get an "all clear — no new {EVENT_LABELS[form.event_type]?.toLowerCase() || 'events'}" email on schedule, so they know the system ran.
                    </p>
                  </div>
                </label>
              </div>
            )}
            <div>
              <Label>Recipients <span className="text-red-500">*</span></Label>
              <RecipientPicker
                pool={recipientPool}
                selected={form.recipients}
                onChange={(recipients) => setForm({ ...form, recipients })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Pick from any personnel or system user ({recipientPool.length} available — includes admins without personnel records).
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} data-testid="save-rule-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button variant="ghost" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        </Card>
      )}

      {/* System notifications — hardcoded scheduled emails surfaced for management */}
      <SystemNotificationsCard
        items={systemNotifs}
        onChange={async (id, patch) => {
          try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/system-notifications/${id}`, patch, {
              headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Saved');
            fetchAll();
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
          }
        }}
        onTrigger={async (endpoint, name) => {
          try {
            const token = localStorage.getItem('token');
            await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`${name} sent`);
            fetchAll();
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to send');
          }
        }}
      />

      <Card className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-muted-foreground" size={32} />
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Bell className="mx-auto text-muted-foreground/50" size={32} />
            <p className="mt-2 text-sm">No rules yet — click "Add Rule" to create your first one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rules-table">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground uppercase">
                  <th className="pb-2 pl-2">Rule</th>
                  <th className="pb-2">Event</th>
                  <th className="pb-2">Scope</th>
                  <th className="pb-2">Delivery</th>
                  <th className="pb-2">Recipients</th>
                  <th className="pb-2">Active</th>
                  <th className="pb-2 text-right pr-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(r => {
                  const dBadge = DELIVERY_BADGES[r.delivery] || { label: r.delivery, class: 'bg-muted' };
                  const isDigest = r.delivery !== 'immediate';
                  return (
                    <tr key={r.id} className={`border-b hover:bg-muted/30 ${!r.is_active ? 'opacity-60' : ''}`} data-testid={`rule-row-${r.id}`}>
                      <td className="py-3 pl-2">
                        <div className="font-medium">{r.name}</div>
                        {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                      </td>
                      <td className="py-3">{EVENT_LABELS[r.event_type] || r.event_type}</td>
                      <td className="py-3 text-muted-foreground">{SCOPE_LABELS[r.scope] || r.scope}</td>
                      <td className="py-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${dBadge.class}`}>
                          {dBadge.label}
                        </span>
                        {isDigest && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {summarizeDays(r.schedule_days_of_week || (r.delivery === 'weekly_digest' ? [r.schedule_day_of_week ?? 0] : [0,1,2,3,4,5,6]))}
                            {' · '}
                            {fmtTime(r.schedule_hour ?? 8, r.schedule_minute ?? 0)}
                          </div>
                        )}
                        {isDigest && r.send_when_empty === false && (
                          <div className="text-[10px] text-amber-700 mt-0.5">only if new items</div>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1 text-xs">
                          <Mail size={12} className="text-muted-foreground" />
                          {(r.recipients || []).length}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">
                          {(r.recipients || []).slice(0, 2).map(x => x.name).join(', ')}
                          {(r.recipients || []).length > 2 && ` +${r.recipients.length - 2}`}
                        </div>
                      </td>
                      <td className="py-3">
                        <Switch
                          checked={r.is_active}
                          onCheckedChange={() => handleToggle(r)}
                          data-testid={`toggle-rule-${r.id}`}
                        />
                      </td>
                      <td className="py-3 pr-2 text-right whitespace-nowrap">
                        {isDigest && (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Send digest now"
                            onClick={() => handleSendNow(r)}
                            data-testid={`send-now-${r.id}`}
                          >
                            <Send size={14} className="text-blue-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(r)} data-testid={`edit-rule-${r.id}`}>
                          <Edit2 size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(r)} data-testid={`delete-rule-${r.id}`}>
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle size={18} className="text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">How rules work</p>
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-blue-800 text-xs">
              <li><b>Immediate</b>: email sent instantly when the event happens.</li>
              <li><b>Daily digest</b>: queued events are bundled into one email at 8:05 AM.</li>
              <li><b>Weekly digest</b>: queued events are bundled into one email Mondays at 8:10 AM.</li>
              <li>Multiple rules can match the same event — each fires independently with its own recipient list.</li>
              <li>Click <Send size={11} className="inline mb-0.5" /> on any digest rule to send the current queue immediately.</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}


// =================================================================
// SYSTEM NOTIFICATIONS CARD
// =================================================================
function SystemNotificationsCard({ items, onChange, onTrigger }) {
  if (!items || items.length === 0) return null;
  return (
    <Card className="p-6 bg-amber-50/40 border-amber-200" data-testid="system-notifications-card">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="text-amber-700" size={18} />
        <h3 className="font-semibold text-lg">System notifications</h3>
        <span className="text-xs text-muted-foreground">
          (built-in scheduled emails — manage recipients &amp; on/off here)
        </span>
      </div>
      <div className="space-y-3">
        {items.map((n) => (
          <SystemNotifRow key={n.id} item={n} onChange={onChange} onTrigger={onTrigger} />
        ))}
      </div>
    </Card>
  );
}

function SystemNotifRow({ item, onChange, onTrigger }) {
  const [recipientsText, setRecipientsText] = useState((item.recipients || []).join(', '));
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => { setRecipientsText((item.recipients || []).join(', ')); }, [item.recipients]);

  const saveRecipients = async () => {
    const list = recipientsText.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    await onChange(item.id, { recipients: list });
    setEditing(false);
  };

  const lastRun = item.last_run_at
    ? new Date(item.last_run_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'never';

  return (
    <div className="bg-white rounded-md border p-4" data-testid={`system-notif-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{item.name}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${item.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
              {item.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
          <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-3">
            <span><b>Schedule:</b> {item.schedule_display}</span>
            <span><b>Last run:</b> {lastRun}{item.last_run_status ? ` (${item.last_run_status})` : ''}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="text-xs flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={!!item.enabled}
              onChange={(e) => onChange(item.id, { enabled: e.target.checked })}
              data-testid={`system-notif-toggle-${item.id}`}
            />
            On
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              setSending(true);
              try { await onTrigger(item.trigger_endpoint, item.name); }
              finally { setSending(false); }
            }}
            disabled={sending || !item.enabled}
            data-testid={`system-notif-trigger-${item.id}`}
            title="Send this notification now"
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          </Button>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t">
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs">Recipients</Label>
          {editing ? (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => { setRecipientsText((item.recipients || []).join(', ')); setEditing(false); }}>Cancel</Button>
              <Button size="sm" onClick={saveRecipients} data-testid={`system-notif-save-${item.id}`}>Save</Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid={`system-notif-edit-${item.id}`}>
              Edit
            </Button>
          )}
        </div>
        {editing ? (
          <textarea
            rows={2}
            className="w-full text-sm border rounded-md px-2 py-1.5 focus-visible:outline-none focus-visible:ring-1"
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            placeholder="email@example.com, another@example.com"
            data-testid={`system-notif-recipients-${item.id}`}
          />
        ) : (
          <div className="text-xs text-muted-foreground">
            {(item.recipients || []).length === 0
              ? <span className="italic text-amber-700">No recipients configured — emails will not deliver</span>
              : (item.recipients || []).join(', ')}
          </div>
        )}
      </div>
    </div>
  );
}
